import { useEffect, useMemo, useState } from 'react';
import api from '../api';

function StockLog() {
  const [stockLogSummary, setStockLogSummary] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('quantityDesc');
  const [days, setDays] = useState(10);
  const [selectedDate, setSelectedDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStockLogSummary = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        search: search || undefined,
        days: selectedDate ? undefined : days,
        dateFrom: selectedDate ? selectedDate : dateFrom || undefined,
        dateTo: selectedDate ? selectedDate : dateTo || undefined
      };

      const response = await api.get('/getStockLogSummary', { params });
      setStockLogSummary(response.data.stockLogSummary || []);
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut incarca istoricul scanarilor'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockLogSummary();
  }, []);

  const filteredItems = useMemo(() => {
    const items = [...stockLogSummary];

    items.sort((a, b) => {
      if (sortBy === 'quantityAsc') {
        return Number(a.totalQuantity || 0) - Number(b.totalQuantity || 0);
      }

      if (sortBy === 'nameAsc') {
        return String(a.productName || '').localeCompare(String(b.productName || ''), 'ro');
      }

      if (sortBy === 'nameDesc') {
        return String(b.productName || '').localeCompare(String(a.productName || ''), 'ro');
      }

      if (sortBy === 'firstScanAsc') {
        return new Date(a.firstScan) - new Date(b.firstScan);
      }

      if (sortBy === 'firstScanDesc') {
        return new Date(b.firstScan) - new Date(a.firstScan);
      }

      if (sortBy === 'lastScanAsc') {
        return new Date(a.lastScan) - new Date(b.lastScan);
      }

      if (sortBy === 'lastScanDesc') {
        return new Date(b.lastScan) - new Date(a.lastScan);
      }

      return Number(b.totalQuantity || 0) - Number(a.totalQuantity || 0);
    });

    return items;
  }, [stockLogSummary, sortBy]);

  const clearExactDate = () => {
    setSelectedDate('');
  };



  const handleUndoLastSaleScan = async () => {
    const ok = window.confirm('Sigur vrei sa anulezi ultima scanare de vanzare?');
    if (!ok) return;

    try {
      setError('');
      await api.post('/undoLastSaleScan');
      await fetchStockLogSummary();
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut face undo pentru ultima scanare'
      );
    }
  };


  const handleDeleteScan = async (logIndex) => {
    const ok = window.confirm('Sigur vrei sa stergi ultima scanare din acest rand?');
    if (!ok) return;

    try {
      setError('');
      await api.delete('/stockLog/' + logIndex);
      await fetchStockLogSummary();
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut sterge scanarea'
      );
    }
  };

  return (
    <div style={pageStyle}>
      <h1 style={titleStyle}>Istoric scanari</h1>
      <div style={{ marginBottom: 16, display: 'flex', gap: 10 }}>
        <button type="button" onClick={handleUndoLastSaleScan} style={undoButtonStyle}>
          Undo ultima scanare
        </button>
      </div>
      <p style={subtitleStyle}>
        Aici vezi doar produsele scanate ca vandute din Start vanzari.
        Poti filtra pe ultimele X zile sau pe o zi exacta.
      </p>

      <div style={cardStyle}>
        <div style={filtersGridStyle}>
          <div>
            <label style={labelStyle}>Zi exacta</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Ultimele X zile</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              style={inputStyle}
              disabled={!!selectedDate}
            >
              <option value={5}>5 zile</option>
              <option value={10}>10 zile</option>
              <option value={20}>20 zile</option>
              <option value={30}>30 zile</option>
              <option value={60}>60 zile</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Data de la</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={inputStyle}
              disabled={!!selectedDate}
            />
          </div>

          <div>
            <label style={labelStyle}>Data pana la</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={inputStyle}
              disabled={!!selectedDate}
            />
          </div>

          <div>
            <label style={labelStyle}>Cautare</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nume produs / cod / cod de bare"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Sortare</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={inputStyle}
            >
              <option value="quantityDesc">Cantitate descrescator</option>
              <option value="quantityAsc">Cantitate crescator</option>
              <option value="lastScanDesc">Ultima scanare cea mai noua</option>
              <option value="lastScanAsc">Ultima scanare cea mai veche</option>
              <option value="firstScanDesc">Prima scanare cea mai noua</option>
              <option value="firstScanAsc">Prima scanare cea mai veche</option>
              <option value="nameAsc">Nume A-Z</option>
              <option value="nameDesc">Nume Z-A</option>
            </select>
          </div>

          <div style={buttonGroupStyle}>
            <button onClick={fetchStockLogSummary} style={buttonStyle}>
              Filtreaza
            </button>

            <button onClick={clearExactDate} style={secondaryButtonStyle}>
              Goleste ziua exacta
            </button>
          </div>
        </div>
      </div>

      {selectedDate ? (
        <div style={infoStyle}>
          Afisezi doar scanarile din data de {formatDateOnly(selectedDate)}.
        </div>
      ) : null}

      {loading ? <div style={infoStyle}>Se incarca istoricul...</div> : null}
      {error ? <div style={errorStyle}>{error}</div> : null}

      <div style={cardStyle}>
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Cod produs</th>
                <th style={thStyle}>Nume produs</th>
                <th style={thStyle}>Cod de bare</th>
                <th style={thStyle}>Cantitate vanduta</th>
                <th style={thStyle}>Prima scanare</th>
                <th style={thStyle}>Ultima scanare</th>
                <th style={thStyle}>Actiuni</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredItems.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan="7">
                    Nu exista scanari pentru criteriile selectate.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td style={tdStyle}>{item.id}</td>
                    <td style={tdStyle}>{item.productName}</td>
                    <td style={tdStyle}>{item.barcode || '-'}</td>
                    <td style={tdStyle}>{item.totalQuantity || 0}</td>
                    <td style={tdStyle}>{formatDate(item.firstScan)}</td>
                    <td style={tdStyle}>{formatDate(item.lastScan)}</td>
                      <td style={tdStyle}>
                        {item.lastLogIndex !== undefined ? (
                          <button
                            type="button"
                            onClick={() => handleDeleteScan(item.lastLogIndex)}
                            style={deleteButtonStyle}
                            title="Sterge ultima scanare"
                          >
                            X
                          </button>
                        ) : '-'}
                      </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateString) {
  if (!dateString) {
    return '-';
  }

  const date = new Date(dateString);

  return date.toLocaleString('ro-RO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function formatDateOnly(dateString) {
  if (!dateString) {
    return '-';
  }

  const date = new Date(dateString);

  return date.toLocaleDateString('ro-RO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
}

const pageStyle = {
  maxWidth: '1400px',
  margin: '0 auto'
};

const titleStyle = {
  marginBottom: '8px',
  fontSize: '32px',
  color: '#111827'
};

const subtitleStyle = {
  marginTop: 0,
  marginBottom: '20px',
  color: '#4b5563'
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  marginBottom: '20px'
};

const filtersGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  alignItems: 'end'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '600',
  color: '#111827'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  fontSize: '15px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  boxSizing: 'border-box'
};

const buttonGroupStyle = {
  display: 'flex',
  gap: '10px',
  alignItems: 'end',
  flexWrap: 'wrap'
};

const buttonStyle = {
  background: '#2563eb',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 18px',
  fontWeight: '700',
  cursor: 'pointer'
};

const secondaryButtonStyle = {
  background: '#6b7280',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 18px',
  fontWeight: '700',
  cursor: 'pointer'
};

const infoStyle = {
  background: '#e0f2fe',
  color: '#075985',
  padding: '12px 16px',
  borderRadius: '10px',
  marginBottom: '16px',
  fontWeight: '600'
};

const errorStyle = {
  background: '#fee2e2',
  color: '#991b1b',
  padding: '12px 16px',
  borderRadius: '10px',
  marginBottom: '16px',
  fontWeight: '600'
};

const tableWrapStyle = {
  overflowX: 'auto'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: '900px'
};

const thStyle = {
  textAlign: 'left',
  padding: '12px',
  background: '#f3f4f6',
  borderBottom: '1px solid #e5e7eb',
  color: '#111827',
  fontSize: '14px',
  whiteSpace: 'nowrap'
};



const undoButtonStyle = {
  background: '#111827',
  color: '#ffffff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  cursor: 'pointer',
  fontWeight: 600
};

const deleteButtonStyle = {
  background: '#dc2626',
  color: '#ffffff',
  border: 'none',
  borderRadius: 6,
  padding: '6px 10px',
  cursor: 'pointer',
  fontWeight: 700
};

const tdStyle = {
  padding: '12px',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '14px',
  color: '#111827',
  whiteSpace: 'nowrap'
};

export default StockLog;
